import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Styles from './style.module.scss'
import Icon from '../icon'

export interface BaseCard {
  id?: string
  [key: string]: any
}

interface Props<T extends BaseCard = BaseCard> {
  cards: T[]
  renderCard?: (card: T) => React.ReactNode
}

const DEFAULT_WIDTH = 280
const DEFAULT_HEIGHT = 280
const DEFAULT_GAP = 24
const DEFAULT_SIDE_BLANK = 48
const TOLERANCE = 2

const DefaultPlaceholder: React.FC = () => (
  <div
    style={{
      width: '100%',
      height: '100%',
      background: '#eee',
      borderRadius: 12,
    }}
  />
)

export default function ScrollGallery<T extends BaseCard>({ cards, renderCard }: Props<T>) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const [dimensions, setDimensions] = useState({
    widths: [] as number[],
    heights: [] as number[],
    gap: DEFAULT_GAP,
    sideBlank: DEFAULT_SIDE_BLANK,
  })

  const maxIdx = cards.length - 1

  const [index, setIndex] = useState(0)
  const [isStart, setIsStart] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  // 测量容器尺寸和卡片尺寸
  useEffect(() => {
    const container = wrapRef.current
    if (!container) return

    const updateDimensions = () => {
      const style = getComputedStyle(container)

      const gapValue = parseInt(style.gap || style.columnGap, 10) || DEFAULT_GAP
      const paddingLeft = parseInt(style.paddingLeft, 10) || DEFAULT_SIDE_BLANK
      const paddingRight = parseInt(style.paddingRight, 10) || DEFAULT_SIDE_BLANK

      const newWidths = cardRefs.current.map(el => (el ? el.offsetWidth : DEFAULT_WIDTH))
      const newHeights = cardRefs.current.map(el => (el ? el.offsetHeight : DEFAULT_HEIGHT))

      setDimensions({
        widths: newWidths,
        heights: newHeights,
        gap: gapValue,
        sideBlank: Math.max(paddingLeft, paddingRight),
      })
    }

    updateDimensions()
    const resizeObserver = new ResizeObserver(updateDimensions)
    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [cards.length])

  // 计算位置
  const positions = useMemo(() => {
    const { widths, gap, sideBlank } = dimensions
    const arr: number[] = []
    let acc = sideBlank
    widths.forEach((w, i) => {
      arr[i] = acc
      acc += w + gap
    })
    return arr
  }, [dimensions, cards.length])

  // 滚动逻辑
  const calcScrollX = useCallback(
    (idx: number) => {
      const el = wrapRef.current
      if (!el) return 0

      const viewW = el.clientWidth
      const left = positions[idx]
      const target = left - (viewW - dimensions.widths[idx]) / 2
      return Math.max(0, Math.min(target, el.scrollWidth - viewW))
    },
    [positions, dimensions.widths]
  )

  const goTo = useCallback(
    (idx: number) => {
      const el = wrapRef.current
      if (!el) return
      el.scrollTo({ left: calcScrollX(idx), behavior: 'smooth' })
    },
    [calcScrollX]
  )

  const goPrev = () => !isStart && goTo(index - 1)
  const goNext = () => !isEnd && goTo(index + 1)

  // 滚动监听
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return

    const update = () => {
      const { scrollLeft, clientWidth, scrollWidth } = el
      const centerX = scrollLeft + clientWidth / 2

      let nearest = 0
      let minDist = Infinity
      positions.forEach((left, i) => {
        const dist = Math.abs(centerX - (left + dimensions.widths[i] / 2))
        if (dist < minDist) {
          minDist = dist
          nearest = i
        }
      })
      setIndex(nearest)

      setIsStart(scrollLeft <= TOLERANCE)
      setIsEnd(scrollLeft + clientWidth >= scrollWidth - TOLERANCE)
    }

    update()
    el.addEventListener('scroll', update, { passive: true })
    return () => el.removeEventListener('scroll', update)
  }, [positions, dimensions.widths])

  // 箭头组件
  const Arrow: React.FC<{ dir: 'left' | 'right' }> = ({ dir }) => (
    <Icon
      name='arrowBlack'
      className={`${Styles.arrow}`}
      style={{ transform: dir === 'left' ? 'rotate(180deg)' : undefined }}
    />
  )

  return (
    <div className={Styles.wrapper}>
      <div ref={wrapRef} className={Styles.container}>
        {cards.map((c, i) => (
          <div
            key={c?.id || i}
            ref={el => (cardRefs.current[i] = el)}
            className={Styles.card}
            style={{ marginRight: i === maxIdx ? 0 : undefined }}
          >
            {renderCard ? renderCard(c) : <DefaultPlaceholder />}
          </div>
        ))}
      </div>

      <div className={Styles.arrowGroup}>
        <button onClick={goPrev} disabled={isStart} className={Styles.arrowBtn}>
          <Arrow dir='left' />
        </button>
        <button onClick={goNext} disabled={isEnd} className={Styles.arrowBtn}>
          <Arrow dir='right' />
        </button>
      </div>
    </div>
  )
}
